/**
 * The quota editor's working copy of a Quotas V2 configuration, and the
 * conversions to and from the API. A rule is a tree of groups and criteria,
 * like the API's (nested to the engine's depth cap, FR-2).
 */
import type {
  QuotaCountOperator,
  QuotaSetAttribute,
  QuotaSetMatch,
  QuotaTargetOperator,
  QuotaV2Dto,
  QuotaV2GroupInputDto,
  QuotaV2RuleConditionNodeDto,
  QuotaV2RuleGroupNodeDto,
  QuotaV2SegmentInputDto,
  QuotaV2TargetDto,
  QuotaV2TargetEntryDto,
  UpsertQuotaDto,
} from '@/api/core-api/types.gen';

export const NO_MATCH_SEGMENT_ID = 'no-match';

/** FR-1 caps, mirrored from the engine so the builder stops before the server refuses. */
export const QUOTA_MAX_GROUPS = 20;
export const QUOTA_MAX_SEGMENTS_PER_GROUP = 50;

export type QuotaConnector = 'AND' | 'OR';
export type QuotaRuleNode =
  | QuotaV2RuleGroupNodeDto
  | QuotaV2RuleConditionNodeDto;
export type QuotaCondition = QuotaV2RuleConditionNodeDto['condition'];

export type DraftSetOp = 'in' | 'notIn';
export type DraftMatch = QuotaSetMatch | 'count';

/**
 * A target being edited. `value` is null while the input is empty, which is
 * only valid for `track`.
 */
export interface DraftTarget {
  op: QuotaTargetOperator;
  value: number | null;
}

interface DraftConditionBase {
  /** Client-only React key. */
  key: string;
}

/** A condition whose source has not been picked yet. */
export interface DraftUnsetCondition extends DraftConditionBase {
  kind: 'unset';
}

export interface DraftAttributeCondition extends DraftConditionBase {
  kind: 'attribute';
  attribute: QuotaSetAttribute;
  op: DraftSetOp;
  values: string[];
}

export interface DraftJobTitleCondition extends DraftConditionBase {
  kind: 'jobTitle';
  op: DraftSetOp;
  /** Job title ids; the server resolves them to title families on save. */
  values: string[];
}

export interface DraftAgeCondition extends DraftConditionBase {
  kind: 'age';
  op: 'range' | 'gt' | 'lt';
  min: number | null;
  max: number | null;
  value: number | null;
}

/** Shared by on-platform and external screener questions. */
export interface DraftQuestionCondition extends DraftConditionBase {
  kind: 'screener' | 'externalScreener';
  /** `questionUid` on-platform, the partner's `questionId` externally. */
  questionId: string;
  match: DraftMatch;
  /** Answer uids on-platform, declared option ids externally. */
  answerIds: string[];
  countOp: QuotaCountOperator;
  countValue: number | null;
}

export type DraftCondition =
  | DraftUnsetCondition
  | DraftAttributeCondition
  | DraftJobTitleCondition
  | DraftAgeCondition
  | DraftQuestionCondition;

/** A group of criteria joined by one connector; groups nest up to `QUOTA_MAX_RULE_DEPTH`. */
export interface DraftRuleGroup {
  /** Client-only React key. */
  key: string;
  kind: 'group';
  connector: QuotaConnector;
  children: DraftRuleNode[];
}

export type DraftRuleNode = DraftCondition | DraftRuleGroup;

/** A segment's rule: its root group. */
export type DraftRule = DraftRuleGroup;

/**
 * Nested rule groups, counting the segment's root group as depth 1: the
 * engine's cap (PX-474), mirrored so a rule written through the API can be
 * edited here as far as it was built.
 */
export const QUOTA_MAX_RULE_DEPTH = 10;

export function isRuleGroup(node: DraftRuleNode): node is DraftRuleGroup {
  return node.kind === 'group';
}

/** Every criterion in the rule, however deep. */
export function ruleConditions(rule: DraftRuleGroup): DraftCondition[] {
  return rule.children.flatMap((child) =>
    isRuleGroup(child) ? ruleConditions(child) : [child],
  );
}

export function newRuleGroup(
  children: DraftRuleNode[] = [newCondition()],
  connector: QuotaConnector = 'AND',
): DraftRuleGroup {
  return { key: newKey('r'), kind: 'group', connector, children };
}

export interface DraftSegment {
  key: string;
  /** Absent until the server mints it on the first save. */
  id?: string;
  name: string;
  /** The name follows the rule until the researcher types one. */
  autoName: boolean;
  rule: DraftRule;
  target: DraftTarget;
}

export interface DraftGroup {
  key: string;
  id?: string;
  name: string;
  autoName: boolean;
  segments: DraftSegment[];
  noMatchTarget: DraftTarget;
  /**
   * The criterion Simple picked (a source key such as `attr:gender`), so a
   * group keeps its question while no answer has a target. Client-only, and
   * only a fallback: the segments' own criterion wins (see
   * `simpleCriterionOf`).
   */
  criterion?: string;
}

export interface DraftQuota {
  groups: DraftGroup[];
}

let keySeed = 0;
/** Client-only keys: unique within the page, never sent to the server. */
export function newKey(prefix: string): string {
  keySeed += 1;
  return `${prefix}-${keySeed}`;
}

export const TRACK_TARGET: DraftTarget = { op: 'track', value: null };

export function newCondition(): DraftUnsetCondition {
  return { key: newKey('c'), kind: 'unset' };
}

export function newSegment(
  conditions: DraftCondition[] = [newCondition()],
): DraftSegment {
  return {
    key: newKey('s'),
    name: '',
    autoName: true,
    rule: newRuleGroup(conditions),
    target: { op: 'min', value: null },
  };
}

export function newGroup(): DraftGroup {
  return {
    key: newKey('g'),
    name: '',
    autoName: true,
    segments: [newSegment()],
    noMatchTarget: TRACK_TARGET,
  };
}

// ─── From the API ──────────────────────────────────────────────────────────

function isConditionNode(
  node: QuotaRuleNode,
): node is QuotaV2RuleConditionNodeDto {
  return node.kind === 'condition';
}

export function conditionToDraft(condition: QuotaCondition): DraftCondition {
  const key = newKey('c');
  switch (condition.source) {
    case 'screener':
    case 'externalScreener': {
      const kind = condition.source;
      const questionId =
        condition.source === 'screener'
          ? condition.questionUid
          : condition.questionId;
      if (condition.match === 'count') {
        return {
          key,
          kind,
          questionId,
          match: 'count',
          answerIds: [],
          countOp: condition.op,
          countValue: condition.value,
        };
      }
      return {
        key,
        kind,
        questionId,
        match: condition.match,
        answerIds: [
          ...(condition.source === 'screener'
            ? condition.answerUids
            : condition.optionIds),
        ],
        countOp: 'gte',
        countValue: null,
      };
    }
    case 'attribute': {
      if (condition.attribute === 'age') {
        return condition.op === 'range'
          ? {
              key,
              kind: 'age',
              op: 'range',
              min: condition.min ?? null,
              max: condition.max ?? null,
              value: null,
            }
          : {
              key,
              kind: 'age',
              op: condition.op,
              min: null,
              max: null,
              value: condition.value,
            };
      }
      if (condition.attribute === 'jobTitle') {
        return {
          key,
          kind: 'jobTitle',
          op: condition.op,
          values: [...condition.values],
        };
      }
      return {
        key,
        kind: 'attribute',
        attribute: condition.attribute,
        op: condition.op,
        values: [...condition.values],
      };
    }
  }
}

export function ruleToDraft(rule: QuotaRuleNode): DraftRule {
  if (isConditionNode(rule)) {
    return newRuleGroup([conditionToDraft(rule.condition)]);
  }
  return newRuleGroup(
    rule.children.map((child) =>
      isConditionNode(child)
        ? conditionToDraft(child.condition)
        : ruleToDraft(child),
    ),
    rule.connector,
  );
}

function targetToDraft(target: QuotaV2TargetDto): DraftTarget {
  return {
    op: target.op,
    value: target.op === 'track' ? null : (target.value ?? null),
  };
}

/**
 * The editor's copy of a saved configuration. Names start as manual; the
 * editor flips `autoName` back on where a stored name equals the generated
 * one, which it can only judge once the value labels are loaded.
 */
export function quotaToDraft(quota: Pick<QuotaV2Dto, 'groups'>): DraftQuota {
  return {
    groups: quota.groups.map((group) => ({
      key: newKey('g'),
      id: group.id,
      name: group.name,
      autoName: false,
      noMatchTarget: targetToDraft(group.noMatch.target),
      segments: group.segments.map((segment) => ({
        key: newKey('s'),
        id: segment.id,
        name: segment.name,
        autoName: false,
        rule: ruleToDraft(segment.rule),
        target: targetToDraft(segment.target),
      })),
    })),
  };
}

// ─── To the API ────────────────────────────────────────────────────────────

/**
 * The API condition for a draft condition, or null while it is incomplete.
 * Completeness here is structural only (a source and its operands exist);
 * whether the values are right is the server's call.
 */
export function draftToCondition(
  condition: DraftCondition,
): QuotaCondition | null {
  switch (condition.kind) {
    case 'unset':
      return null;
    case 'attribute':
      return {
        source: 'attribute',
        attribute: condition.attribute,
        op: condition.op,
        values: condition.values,
      };
    case 'jobTitle':
      // `resolvedFamilyGroupIds` and `groupingVersion` are server-set and
      // ignored on input; the generated read type marks them required.
      return {
        source: 'attribute',
        attribute: 'jobTitle',
        op: condition.op,
        values: condition.values,
        resolvedFamilyGroupIds: [],
        groupingVersion: 0,
      };
    case 'age':
      if (condition.op === 'range') {
        return {
          source: 'attribute',
          attribute: 'age',
          op: 'range',
          ...(condition.min !== null && { min: condition.min }),
          ...(condition.max !== null && { max: condition.max }),
        };
      }
      return condition.value === null
        ? null
        : {
            source: 'attribute',
            attribute: 'age',
            op: condition.op,
            value: condition.value,
          };
    case 'screener':
    case 'externalScreener': {
      if (!condition.questionId) return null;
      if (condition.match === 'count') {
        if (condition.countValue === null) return null;
        return condition.kind === 'screener'
          ? {
              source: 'screener',
              questionUid: condition.questionId,
              match: 'count',
              op: condition.countOp,
              value: condition.countValue,
            }
          : {
              source: 'externalScreener',
              questionId: condition.questionId,
              match: 'count',
              op: condition.countOp,
              value: condition.countValue,
            };
      }
      return condition.kind === 'screener'
        ? {
            source: 'screener',
            questionUid: condition.questionId,
            match: condition.match,
            answerUids: condition.answerIds,
          }
        : {
            source: 'externalScreener',
            questionId: condition.questionId,
            match: condition.match,
            optionIds: condition.answerIds,
          };
    }
  }
}

function draftToRule(rule: DraftRuleGroup): QuotaV2RuleGroupNodeDto {
  return {
    kind: 'group',
    connector: rule.connector,
    children: rule.children.flatMap((child): QuotaRuleNode[] => {
      if (isRuleGroup(child)) return [draftToRule(child)];
      const apiCondition = draftToCondition(child);
      return apiCondition
        ? [{ kind: 'condition', condition: apiCondition }]
        : [];
    }),
  };
}

function draftToTarget(target: DraftTarget): QuotaV2TargetDto {
  if (target.op === 'track') return { op: 'track' };
  return { op: target.op, value: target.value ?? 0 };
}

/** `name` is resolved by the caller: a generated name when `autoName` is on. */
export function draftToUpsert(
  draft: DraftQuota,
  names: {
    group: (group: DraftGroup) => string;
    segment: (segment: DraftSegment, group: DraftGroup) => string;
  },
): UpsertQuotaDto {
  return {
    version: 'v2',
    groups: draft.groups.map(
      (group): QuotaV2GroupInputDto => ({
        ...(group.id && { id: group.id }),
        name: names.group(group),
        noMatch: { target: draftToTarget(group.noMatchTarget) },
        segments: group.segments.map(
          (segment): QuotaV2SegmentInputDto => ({
            ...(segment.id && { id: segment.id }),
            name: names.segment(segment, group),
            rule: draftToRule(segment.rule),
            target: draftToTarget(segment.target),
          }),
        ),
      }),
    ),
  };
}

/**
 * The target-only edit between a saved configuration and an upsert body, or
 * null when anything else changed. A target-only edit goes through
 * `PATCH …/targets`, which leaves every participant's segment alone; any other
 * change is an upsert that re-buckets everyone (FR-13).
 */
export function targetOnlyChanges(
  saved: Pick<QuotaV2Dto, 'groups'>,
  next: UpsertQuotaDto,
): QuotaV2TargetEntryDto[] | null {
  if (saved.groups.length !== next.groups.length) return null;
  const entries: QuotaV2TargetEntryDto[] = [];

  for (const [index, group] of next.groups.entries()) {
    const before = saved.groups[index];
    if (!before || group.id !== before.id || group.name !== before.name)
      return null;
    if (group.segments.length !== before.segments.length) return null;
    for (const [segmentIndex, segment] of group.segments.entries()) {
      const previous = before.segments[segmentIndex];
      if (
        !previous ||
        segment.id !== previous.id ||
        segment.name !== previous.name ||
        !sameRule(segment.rule, previous.rule)
      ) {
        return null;
      }
      if (!sameTarget(segment.target, previous.target)) {
        entries.push({
          groupId: before.id,
          segmentId: previous.id,
          target: segment.target,
        });
      }
    }
    const noMatchTarget = group.noMatch?.target ?? { op: 'track' };
    if (!sameTarget(noMatchTarget, before.noMatch.target)) {
      entries.push({
        groupId: before.id,
        segmentId: NO_MATCH_SEGMENT_ID,
        target: noMatchTarget,
      });
    }
  }
  return entries;
}

function sameTarget(a: QuotaV2TargetDto, b: QuotaV2TargetDto): boolean {
  return a.op === b.op && (a.op === 'track' || a.value === b.value);
}

/** Structural rule equality, ignoring the server-set job title fields. */
export function sameRule(a: QuotaRuleNode, b: QuotaRuleNode): boolean {
  return JSON.stringify(normalizeRule(a)) === JSON.stringify(normalizeRule(b));
}

function normalizeRule(node: QuotaRuleNode): unknown {
  if (isConditionNode(node)) {
    // A lone condition and a one-child group match the same people; the
    // editor always writes the group form.
    return normalizeRule({ kind: 'group', connector: 'AND', children: [node] });
  }
  return {
    connector: node.children.length > 1 ? node.connector : 'AND',
    children: node.children.map((child) =>
      isConditionNode(child)
        ? normalizeCondition(child.condition)
        : normalizeRule(child),
    ),
  };
}

function normalizeCondition(condition: QuotaCondition): unknown {
  if (condition.source === 'attribute' && condition.attribute === 'jobTitle') {
    return {
      source: condition.source,
      attribute: condition.attribute,
      op: condition.op,
      values: condition.values,
    };
  }
  const entries = Object.entries(condition).toSorted(([a], [b]) =>
    a.localeCompare(b),
  );
  return Object.fromEntries(entries);
}
